import React from 'react'
import type { Captain } from 'types/fpl/elements'
import type { Chip } from 'types/fpl/chips'
import { CHIP_LABEL, CHIP_ICON } from 'types/fpl/chips'
import type { InterestScore, LeagueTemplate, TemplatinessStats } from 'api/league'
import type { LeagueTransferStats } from 'types/fpl/transfers'
import { VERDICT_CONFIG } from 'constants/interest'
import { getScoreColor } from 'utils/scoreColor'
import { formatDecimal } from 'utils/numbers'
import { captainPct, resolveLeagueTeamCount } from 'utils/leagueTeamCount'
import ExportTemplatePitch from 'components/export/ExportTemplatePitch'
import InterestScoreBadge from 'components/structure/InterestScoreBadge'
import FPL_CHERMESH_LOGO from 'assets/images/fpl-chermesh-logo.png'
import { colors } from 'constants/colors'

interface ExportPreviewProps {
  leagueName: string
  gameweek: string
  captains: Captain[]
  chips: Chip[]
  totalTeams: number
  isDarkMode?: boolean
  shirtImages?: Record<number, string>
  gkShirtImages?: Record<number, string>
  interest?: InterestScore | null
  leagueTemplate?: LeagueTemplate | null
  templatiness?: TemplatinessStats | null
  transferStats?: LeagueTransferStats | null
}

const PITCH_COL_W = 420

const ExportPreview: React.FC<ExportPreviewProps> = ({
  leagueName,
  gameweek,
  captains,
  chips,
  totalTeams,
  isDarkMode = false,
  shirtImages = {},
  gkShirtImages = {},
  interest,
  leagueTemplate,
  templatiness,
  transferStats,
}) => {
  const sortedCaptains = [...captains].sort((a, b) => b.count - a.count)
  const sortedChips    = [...chips].sort((a, b) => b.count - a.count)
  const leagueSize = resolveLeagueTeamCount(totalTeams, captains)

  const bgColor       = isDarkMode ? colors.RICH_BLACK  : colors.MOONSTONE
  const textColor     = isDarkMode ? colors.WHITE       : colors.ALMOST_BLACK
  const paperBg       = isDarkMode ? colors.ALMOST_BLACK : colors.WHITE
  const borderColor   = isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(224,224,224,1)'
  const paperShadow   = '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px rgba(0,0,0,0.14),0px 1px 3px rgba(0,0,0,0.12)'

  const thStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
    padding: '5px 10px',
    textAlign: 'left',
    fontFamily: 'PremierLeagueBold, Arial, sans-serif',
    fontSize: '0.8rem',
    color: textColor,
    fontWeight: 600,
    borderBottom: `1px solid ${borderColor}`,
    ...extra,
  })

  const tdStyle = (bold = false, extra?: React.CSSProperties): React.CSSProperties => ({
    padding: '4px 10px',
    borderBottom: `1px solid ${borderColor}`,
    fontFamily: bold ? 'PremierLeagueBold, Arial, sans-serif' : 'PremierLeagueRegular, Arial, sans-serif',
    fontSize: '0.8rem',
    color: textColor,
    ...extra,
  })

  const lastRowTd = (bold = false, extra?: React.CSSProperties): React.CSSProperties => ({
    ...tdStyle(bold, extra),
    borderBottom: 'none',
  })

  const paperCard = (extra?: React.CSSProperties): React.CSSProperties => ({
    backgroundColor: paperBg,
    borderRadius: 4,
    padding: '10px 12px',
    boxShadow: paperShadow,
    ...extra,
  })

  const sectionTitle = (text: string) => (
    <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontFamily: 'PremierLeagueBold, Arial, sans-serif', color: textColor, fontWeight: 500 }}>
      {text}
    </h3>
  )

  // ── Interest card (metrics + summary, score badge top-right) ──
  const interestCfg = interest ? VERDICT_CONFIG[interest.verdict] ?? VERDICT_CONFIG.ok : null
  const interestScoreColor = interest ? getScoreColor(interest.total_interest_score) : undefined

  const interestDetailsCard = interest && interestScoreColor && interestCfg ? (() => {
    const xfer = interest.transfer_activity ?? 0
    const metrics = [
      { label: 'Capt. Diversity', value: Math.round(interest.captain_diversity * 10), color: getScoreColor(interest.captain_diversity * 10) },
      { label: 'Differentials', value: Math.round(interest.differentials_captains * 10), color: getScoreColor(interest.differentials_captains * 10) },
      { label: 'Chip Activity', value: Math.round(interest.chips_usability * 10), color: getScoreColor(interest.chips_usability * 10) },
      { label: 'Transfers', value: Math.round(xfer * 10), color: getScoreColor(xfer * 10) },
    ]
    return (
      <div style={paperCard()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontFamily: 'PremierLeagueBold, Arial, sans-serif', color: textColor, fontWeight: 500 }}>
            Interest Score
          </h3>
          <InterestScoreBadge score={interest.total_interest_score} verdict={interest.verdict} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          {metrics.map(({ label, value, color: c }) => (
            <div key={label} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontFamily: 'PremierLeagueRegular, Arial, sans-serif', fontSize: '0.52rem', opacity: 0.6, marginBottom: 2, lineHeight: 1.15 }}>{label}</div>
              <div style={{ fontFamily: 'PremierLeagueBold, Arial, sans-serif', fontSize: '0.8rem', color: c }}>{value}</div>
            </div>
          ))}
        </div>
        <div
          style={{
            padding: '4px 7px 4px 8px',
            borderRadius: 3,
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)',
            borderLeft: `2.5px solid ${interestScoreColor}`,
          }}
        >
          <div
            style={{
              fontFamily: 'PremierLeagueRegular, Arial, sans-serif',
              fontSize: '0.65rem',
              fontStyle: 'italic',
              opacity: 0.8,
              lineHeight: '1.35',
              margin: 0,
            }}
          >
            {interest.summary}
          </div>
        </div>
      </div>
    )
  })() : null

  return (
    <div
      id="export-preview"
      style={{
        width: '1100px',
        backgroundColor: bgColor,
        padding: '36px 40px 40px',
        fontFamily: 'PremierLeagueRegular, Arial, sans-serif',
        color: textColor,
        boxSizing: 'border-box',
        border: `5px solid ${borderColor}`,
        position: 'relative',
      }}
    >
      {/* ── Header row: logo | centered title ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '48px 1fr 48px',
          gap: 12,
          alignItems: 'start',
          marginBottom: 28,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: colors.ALMOST_BLACK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 10,
            boxSizing: 'border-box',
            opacity: 0.7,
          }}
        >
          <img src={FPL_CHERMESH_LOGO} alt="FPL Chermesh Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>

        <div style={{ textAlign: 'center', alignSelf: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '32px', fontFamily: 'PremierLeagueBold, Arial, sans-serif', color: textColor, lineHeight: 1.2 }}>
            {leagueName}
          </h1>
          <h2 style={{ margin: '8px 0 0', fontSize: '18px', fontFamily: 'PremierLeagueRegular, Arial, sans-serif', color: textColor, fontWeight: 'normal' }}>
            GW {gameweek} insights
          </h2>
        </div>
      </div>

      {/* Two columns: tables left | interest + pitch right */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

        {/* LEFT — tables only */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={paperCard()}>
            {sectionTitle('Captains')}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '6%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 4px' })}> </th>
                  <th style={thStyle({ padding: '5px 8px' })}>Player</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 8px', whiteSpace: 'nowrap' })}>Captained</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 8px', whiteSpace: 'nowrap' })}>Captained %</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 8px', whiteSpace: 'nowrap' })}>TSB%</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 8px', whiteSpace: 'nowrap' })}>LSB%</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 8px', whiteSpace: 'nowrap' })}>Triple</th>
                </tr>
              </thead>
              <tbody>
                {sortedCaptains.map((c, i) => {
                  const pct = formatDecimal(captainPct(c.count, totalTeams, captains))
                  const tsb = formatDecimal(parseFloat(c.player.selected_by_percent ?? '0') || 0)
                  const lsbRaw =
                    typeof c.starts_league_pct === 'number'
                      ? c.starts_league_pct
                      : leagueSize && typeof c.starts_league_count === 'number'
                        ? (c.starts_league_count / leagueSize) * 100
                        : 0
                  const lsb = formatDecimal(lsbRaw)
                  const cell = i === sortedCaptains.length - 1 ? lastRowTd : tdStyle
                  const numCell = (bold = false) => cell(bold, { textAlign: 'center', padding: '4px 8px', whiteSpace: 'nowrap' })
                  return (
                    <tr key={c.player.id}>
                      <td style={cell(false, { textAlign: 'center', padding: '4px 4px' })}>
                        {shirtImages[c.player.team] && (
                          <img src={shirtImages[c.player.team]} alt="" style={{ width: 20, height: 20, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                        )}
                      </td>
                      <td style={cell(true, { padding: '4px 8px' })}>{c.player.web_name}</td>
                      <td style={numCell(true)}>{c.count}</td>
                      <td style={numCell()}>{pct}%</td>
                      <td style={numCell()}>{tsb}%</td>
                      <td style={numCell()}>{lsb}%</td>
                      <td style={numCell()}>{c.triple}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={paperCard()}>
            {sectionTitle('Chips')}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '52%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '24%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle({ padding: '5px 8px' })}>Chip</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 10px', whiteSpace: 'nowrap' })}>Used</th>
                  <th style={thStyle({ textAlign: 'center', padding: '5px 10px', whiteSpace: 'nowrap' })}>Used %</th>
                </tr>
              </thead>
              <tbody>
                {sortedChips.map((ch, i) => {
                  const pct = leagueSize ? formatDecimal((ch.count / leagueSize) * 100) : 0
                  const cell = i === sortedChips.length - 1 ? lastRowTd : tdStyle
                  return (
                    <tr key={ch.chip}>
                      <td style={cell(true, { padding: '4px 8px' })}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {CHIP_ICON[ch.chip] && <img src={CHIP_ICON[ch.chip]} alt="" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />}
                          {CHIP_LABEL[ch.chip]}
                        </div>
                      </td>
                      <td style={cell(true, { textAlign: 'center', padding: '4px 10px', whiteSpace: 'nowrap' })}>{ch.count}</td>
                      <td style={cell(false, { textAlign: 'center', padding: '4px 10px', whiteSpace: 'nowrap' })}>{pct}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {transferStats ? (() => {
            const distribution = transferStats.penalty_cost_distribution ?? []
            const hasMinuses = transferStats.penalties_count > 0
            const avgMinus = transferStats.avg_hit_cost ?? 0
            const teamLabel = (count: number) => (count === 1 ? '1 team' : `${count} teams`)

            const tileStyle = (
              gradient: string,
              accent: string,
              valueColor?: string,
            ): React.CSSProperties => ({
              position: 'relative',
              flex: '1 1 0',
              minWidth: 0,
              borderRadius: 8,
              padding: '10px 12px',
              background: gradient,
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,34,41,0.08)'}`,
              boxShadow: isDarkMode
                ? '0 4px 14px rgba(0,0,0,0.22)'
                : '0 2px 8px rgba(0,34,41,0.06)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              ...(valueColor ? {} : {}),
            })

            const tileLabel: React.CSSProperties = {
              fontFamily: 'PremierLeagueBold, Arial, sans-serif',
              fontSize: '0.52rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              opacity: 0.65,
              lineHeight: 1.2,
              position: 'relative',
              zIndex: 1,
            }

            const tileValue = (color?: string): React.CSSProperties => ({
              fontFamily: 'PremierLeagueBold, Arial, sans-serif',
              fontSize: '1.2rem',
              lineHeight: 1.1,
              color: color ?? textColor,
              position: 'relative',
              zIndex: 1,
            })

            const tileSub: React.CSSProperties = {
              fontFamily: 'PremierLeagueRegular, Arial, sans-serif',
              fontSize: '0.64rem',
              opacity: 0.65,
              lineHeight: 1.2,
              position: 'relative',
              zIndex: 1,
            }

            const tiles = [
              {
                key: 'rolled',
                label: 'Rolled a transfer',
                value: String(transferStats.save_transfer_count),
                sub: `${formatDecimal(transferStats.save_transfer_pct)}% of league`,
                gradient: isDarkMode
                  ? 'linear-gradient(135deg, rgba(49,175,180,0.32) 0%, rgba(0,34,41,0.94) 100%)'
                  : 'linear-gradient(135deg, rgba(49,175,180,0.28) 0%, rgba(255,255,255,0.96) 100%)',
                accent: colors.MOONSTONE,
              },
              {
                key: 'avg',
                label: 'Avg. transfers',
                value: formatDecimal(transferStats.avg_transfers),
                sub: 'per team this GW',
                gradient: isDarkMode
                  ? 'linear-gradient(135deg, rgba(100,149,237,0.3) 0%, rgba(0,34,41,0.92) 100%)'
                  : 'linear-gradient(135deg, rgba(100,149,237,0.26) 0%, rgba(255,255,255,0.96) 100%)',
                accent: '#5B7FD1',
              },
              {
                key: 'minus',
                label: 'Avg. minus',
                value: avgMinus > 0 ? `-${formatDecimal(avgMinus)}` : '0',
                sub: hasMinuses
                  ? `${formatDecimal(transferStats.penalties_pct)}% took a minus`
                  : 'No paid transfers',
                gradient: hasMinuses
                  ? isDarkMode
                    ? 'linear-gradient(135deg, rgba(180,54,49,0.28) 0%, rgba(0,34,41,0.94) 100%)'
                    : 'linear-gradient(135deg, rgba(180,54,49,0.2) 0%, rgba(255,255,255,0.96) 100%)'
                  : isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(0,34,41,0.92) 100%)'
                    : 'linear-gradient(135deg, rgba(0,34,41,0.06) 0%, rgba(255,255,255,0.96) 100%)',
                accent: hasMinuses ? colors.CARMINE_RED : '#8a8a8a',
                valueColor: hasMinuses ? colors.CARMINE_RED : undefined,
              },
            ]

            return (
              <div style={paperCard()}>
                {sectionTitle('Transfers')}
                <div style={{ display: 'flex', gap: 10 }}>
                  {tiles.map((tile) => (
                    <div key={tile.key} style={tileStyle(tile.gradient, tile.accent, tile.valueColor)}>
                      <div
                        style={{
                          position: 'absolute',
                          top: -18,
                          right: -18,
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          background: tile.accent,
                          opacity: isDarkMode ? 0.12 : 0.16,
                          pointerEvents: 'none',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          height: 3,
                          width: '100%',
                          background: `linear-gradient(90deg, ${tile.accent} 0%, transparent 65%)`,
                          opacity: 0.7,
                          pointerEvents: 'none',
                        }}
                      />
                      <div style={tileLabel}>{tile.label}</div>
                      <div style={tileValue(tile.valueColor)}>{tile.value}</div>
                      <div style={tileSub}>{tile.sub}</div>
                    </div>
                  ))}
                </div>

                {distribution.length > 0 ? (
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        fontFamily: 'PremierLeagueBold, Arial, sans-serif',
                        fontSize: '0.58rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        opacity: 0.65,
                        marginBottom: 8,
                      }}
                    >
                      Minus breakdown
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {distribution.map((bucket) => {
                        const pct = leagueSize ? (bucket.count / leagueSize) * 100 : 0
                        return (
                          <div
                            key={bucket.points}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              borderRadius: 6,
                              padding: '7px 10px',
                              background: isDarkMode ? 'rgba(180,54,49,0.1)' : 'rgba(180,54,49,0.06)',
                              border: `1px solid ${isDarkMode ? 'rgba(180,54,49,0.22)' : 'rgba(180,54,49,0.14)'}`,
                            }}
                          >
                            <div
                              style={{
                                fontFamily: 'PremierLeagueBold, Arial, sans-serif',
                                fontSize: '0.9rem',
                                color: colors.CARMINE_RED,
                                minWidth: 32,
                                lineHeight: 1,
                              }}
                            >
                              −{bucket.points}
                            </div>
                            <div
                              style={{
                                fontFamily: 'PremierLeagueRegular, Arial, sans-serif',
                                fontSize: '0.72rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {teamLabel(bucket.count)}
                              {leagueSize ? (
                                <>
                                  {' · '}
                                  <span style={{ fontFamily: 'PremierLeagueBold, Arial, sans-serif' }}>
                                    {formatDecimal(pct)}%
                                  </span>
                                  {' of league'}
                                </>
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })() : null}
        </div>

        {/* RIGHT — interest details + pitch */}
        <div style={{ width: PITCH_COL_W, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {interestDetailsCard}

          {leagueTemplate?.players?.length ? (
            <div style={{ ...paperCard(), padding: '8px 8px 6px' }}>
              <ExportTemplatePitch
                template={leagueTemplate}
                templatiness={templatiness ?? null}
                totalTeams={leagueSize}
                shirtImages={shirtImages}
                gkShirtImages={gkShirtImages}
                isDarkMode={isDarkMode}
                embedded
              />
            </div>
          ) : null}
        </div>

      </div>
    </div>
  )
}

export default ExportPreview
